import { isJwtExpired } from '@/utils/jwt'

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  token?: string
}

export type BlobRequestOptions = Omit<RequestInit, 'body'> & {
  token?: string
}

// Em produção, apiUrl vem de window.__NAVEGA_CONFIG__ (injetado pelo server.mjs via /config.js).
// Em dev, apiUrl é vazio e o Vite proxy encaminha /api/* para o backend sem CORS.
function createApiClient(prefix: string) {
  const BASE_URL = (window.__NAVEGA_CONFIG__?.apiUrl ?? import.meta.env.VITE_API_URL ?? '') + prefix

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, token, headers, ...rest } = options

    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (response.status === 204) return undefined as T

    const json = await response.json()

    if (!response.ok) {
      if (response.status === 401 && token && isJwtExpired(token)) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
      )
    }

    return json.data as T
  }

  async function requestBlob(path: string, options: BlobRequestOptions = {}): Promise<Blob> {
    const { token, headers, ...rest } = options
    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    })
    if (!response.ok) {
      const json = await response.json().catch(() => ({}))
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
      )
    }
    return response.blob()
  }

  async function requestMultipart<T>(
    path: string,
    body: FormData,
    options: BlobRequestOptions = {},
  ): Promise<T> {
    const { token, headers, ...rest } = options
    const response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      method: 'POST',
      // Content-Type must NOT be set manually for FormData — browser adds the boundary
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body,
    })
    if (response.status === 204) return undefined as T
    const json = await response.json()
    if (!response.ok) {
      if (response.status === 401 && token && isJwtExpired(token)) {
        window.dispatchEvent(new CustomEvent('navega:session-expired'))
      }
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
      )
    }
    return json.data as T
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { method: 'GET', ...options }),

    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'POST', body, ...options }),

    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'PUT', body, ...options }),

    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'PATCH', body, ...options }),

    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { method: 'DELETE', ...options }),

    getBlob: (path: string, options?: BlobRequestOptions) =>
      requestBlob(path, { method: 'GET', ...options }),

    postMultipart: <T>(path: string, body: FormData, options?: BlobRequestOptions) =>
      requestMultipart<T>(path, body, options),
  }
}

export const apiClient = createApiClient('/api/v1')
export const apiV2Client = createApiClient('/api/v2')
